import React, { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import GoalFields from './GoalFields'
import FormComponent from '../../../../../components/Form'
import FieldComponent from '../../../../../components/Form/Field'
import FieldLabelWrapperComponent from '../../../../../features/components/Form/FieldLabelWrapper'
import AppPromotionIos14Fields from '../../../../Facebook/UploadFacebookAdCreative/UploadFacebookAdCreativeForm/CampaignForm/NewCampaignFields/AppPromotionIos14Fields'
import BudgetFieldsWrapper from './BudgetFieldsWrapper'
import PlatformsFieldWrapper from './PlatformsFieldWrapper'
import CampaignLevelBudgetToggle from './CampaignLevelBudgetToggle'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useDoubleRequestSubmit from '../../../../../hooks/formHooks/useDoubleRequestSubmit'
import { getShowBudgetSection } from './helper'

import { rightSelector } from '../../../../../modules/selectors/rights'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'

import { validationSchema } from './validation'
import { getInitialValues, NAME } from './fields'

import { COMBINED_FACEBOOK_TIKTOK_CREATE } from '../../../../../constants/forms'
import { FACEBOOK_PROVIDER_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import {
  selectedFacebookAdAccountSelector,
  selectedTikTokAdAccountSelector
} from '../../../../../modules/selectors/adAccounts'
import { OBJECTIVE } from '../../../CampaignForms/fields'
import { AUTO_OPTIMIZED_CAMPAIGN_BUDGET } from '../../../../reusableFields'

// Type-safe aliases to avoid TypeScript errors with JS components
const Field = FieldComponent as any
const Form = FormComponent as any
const FieldLabelWrapper = FieldLabelWrapperComponent as any

interface CampaignCreateFormProps {
  onSuccess: () => void
}

const CampaignCreateForm: React.FC<CampaignCreateFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation()
  const facebookCreateCampaign = useCallback(() => {
    console.log('facebookCreateCampaign')
  }, [])

  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const right = useSelector(rightSelector)
  const { id: selectedFacebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { id: selectedTikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const isBothPlatformsLinked = selectedFacebookAdAccountId && selectedTikTokAdAccountId

  const { allocation_type: rightAllocationType } = right
  const { campaigns = [] } = combinedCampaigns
  const facebookCampaigns = useMemo(
    () => campaigns.filter((campaign: { provider: string }) => campaign.provider === FACEBOOK_PROVIDER_PLATFORM),
    [campaigns]
  )
  const isCampaignsExist = !!facebookCampaigns.length

  const facebookCampaignWasCreatedSelector = useCallback(() => false, [])
  const facebookCampaignErrorSelector = useCallback(() => false, [])
  const facebookCampaignDataSelector = useCallback(() => false, [])

  const facebookCampaignCreateRequest = useMemo(
    () => ({
      requestHandler: facebookCreateCampaign,
      errorSelector: facebookCampaignErrorSelector,
      successSelector: facebookCampaignWasCreatedSelector,
      dataSelector: facebookCampaignDataSelector
    }),
    [
      facebookCreateCampaign,
      facebookCampaignErrorSelector,
      facebookCampaignWasCreatedSelector,
      facebookCampaignDataSelector
    ]
  )

  const tikTokCreateCampaign = useCallback(() => {
    console.log('tikTokCreateCampaign')
  }, [])

  const tikTokCampaignWasCreatedSelector = useCallback(() => false, [])
  const tikTokCampaignErrorSelector = useCallback(() => false, [])
  const tikTokCampaignDataSelector = useCallback(() => false, [])

  const tikTokCampaignCreateRequest = useMemo(
    () => ({
      requestHandler: tikTokCreateCampaign,
      errorSelector: tikTokCampaignErrorSelector,
      successSelector: tikTokCampaignWasCreatedSelector,
      dataSelector: tikTokCampaignDataSelector
    }),
    [tikTokCreateCampaign, tikTokCampaignErrorSelector, tikTokCampaignWasCreatedSelector, tikTokCampaignDataSelector]
  )

  const requests = useMemo(
    () => [facebookCampaignCreateRequest, tikTokCampaignCreateRequest],
    [facebookCampaignCreateRequest, tikTokCampaignCreateRequest]
  )

  // todo make the useDoubleRequestSubmit or analogue hook to make 2 requests simultaneously(currently it goes one after another)
  const { onSubmit, onClearSubmit, isLoadingSelector, errorSelector, successSubmit } = useDoubleRequestSubmit({
    requests,
    // todo
    clearSubmitHandler: () => console.log('clearSubmitHandler')
  })

  const initialValues = useMemo(
    () =>
      getInitialValues({
        rightAllocationType,
        isCampaignsExist,
        selectedPartnership,
        selectedFacebookAdAccountId,
        selectedTikTokAdAccountId
      }),
    [isCampaignsExist, rightAllocationType, selectedPartnership, selectedFacebookAdAccountId, selectedTikTokAdAccountId]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values } = purifiedFormik

  const showBudgetSection = getShowBudgetSection(values[OBJECTIVE])

  return (
    <Form
      formik={purifiedFormik}
      formName={COMBINED_FACEBOOK_TIKTOK_CREATE}
      // processing
      successSubmit={successSubmit}
      errorSelector={errorSelector}
      isLoadingSelector={isLoadingSelector}
      // after form submit
      clearHandler={onClearSubmit}
      onSuccessSubmit={onSuccess}
    >
      {isBothPlatformsLinked && <PlatformsFieldWrapper formik={formik} />}
      <FieldLabelWrapper label={t('Campaign name*')} labelFor={NAME} horizontalView={true}>
        <Field formik={formik} id={NAME} name={NAME} placeholder="Name" />
      </FieldLabelWrapper>
      <GoalFields formik={formik} />
      {showBudgetSection && <CampaignLevelBudgetToggle formik={formik} />}
      <AppPromotionIos14Fields formik={formik} />
      {showBudgetSection && values[AUTO_OPTIMIZED_CAMPAIGN_BUDGET] && <BudgetFieldsWrapper formik={formik} />}
    </Form>
  )
}

export default CampaignCreateForm
